import { useState } from 'react'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick-theme.css'
import 'slick-carousel/slick/slick.css'

import { SanityImage } from 'src/components/atoms/SanityImage'
import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'

import { QuoteRebrand } from 'src/config/generated/sanitySchema'

type QuoteItem = NonNullable<QuoteRebrand['quotes']>[number]

type BackgroundKey = NonNullable<QuoteItem['backgroundColor']>

const BACKGROUND_CLASSES: Record<BackgroundKey, string> = {
  terracotta: 'bg-terracotta',
  forestDeep: 'bg-forestDeep',
  espresso: 'bg-espresso',
  wineRich: 'bg-wineRich'
}

// Active dot color follows the active slide's background.
const ACTIVE_DOT_CLASSES: Record<BackgroundKey, string> = {
  terracotta: '[&_.slick-dots_li.slick-active_button::before]:text-limeBright!',
  forestDeep: '[&_.slick-dots_li.slick-active_button::before]:text-limeBright!',
  espresso: '[&_.slick-dots_li.slick-active_button::before]:text-limeBright!',
  wineRich:
    '[&_.slick-dots_li.slick-active_button::before]:text-lavenderBright!'
}

const CAROUSEL_SETTINGS = {
  arrows: false,
  dots: true,
  infinite: true,
  autoplay: true,
  autoplaySpeed: 8000,
  pauseOnHover: true,
  speed: 400,
  slidesToShow: 1,
  slidesToScroll: 1,
  swipe: true,
  swipeToSlide: true
}

const QuoteCard = ({ data }: { data: QuoteItem }) => {
  const bgClass =
    BACKGROUND_CLASSES[data.backgroundColor as BackgroundKey] ||
    BACKGROUND_CLASSES.terracotta

  const quoteColor =
    data.backgroundColor === 'forestDeep' ? 'text-limeBright' : 'text-linen'

  return (
    <div className={`${bgClass} h-full flex flex-col justify-center`}>
      <div className='container mx-auto px-4 lg:px-20 flex flex-col gap-4 pt-14 pb-18'>
        <span
          aria-hidden
          className={`block leading-[0.5] opacity-40 text-[28px] lg:text-[50px] italic font-semibold ${quoteColor} -mb-5 md:-mb-8`}
        >
          &quot;
        </span>
        <p
          className={`font-body-rebrand text-[20px] lg:text-[32px] leading-[1.4] ${quoteColor} pl-2 md:pl-6 lg:pl-8`}
        >
          {data.quote}
          <span
            aria-hidden
            className='inline-block leading-[0] align-baseline opacity-40 text-[28px] lg:text-[50px] italic font-semibold'
          >
            &quot;
          </span>
        </p>

        {(data.avatar || data.personName || data.personRole) && (
          <div className='flex items-center gap-3 lg:gap-[26px] mt-2 pl-4 md:pl-8 lg:pl-10'>
            {data.avatar && (
              <SanityImage
                source={data.avatar}
                alt={data.personName || 'Avatar'}
                width={62}
                height={62}
                sourceWidth={124}
                sourceHeight={124}
                className='size-[42px] lg:size-[62px] rounded-full object-cover shrink-0'
              />
            )}
            {(data.personName || data.personRole) && (
              <div className='flex flex-col gap-1'>
                {data.personName && (
                  <TypographyRebrand variant='h5' color='linen'>
                    {data.personName}
                  </TypographyRebrand>
                )}
                {data.personRole && (
                  <TypographyRebrand variant='paragraphSmall' color='linen'>
                    {data.personRole}
                  </TypographyRebrand>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export interface QuoteSectionRebrandProps {
  quotes?: QuoteItem[]
}

const QuoteSectionRebrand = ({ quotes }: QuoteSectionRebrandProps) => {
  const [activeIndex, setActiveIndex] = useState(0)

  if (!quotes?.length) return null

  const activeDotClass =
    ACTIVE_DOT_CLASSES[quotes[activeIndex]?.backgroundColor as BackgroundKey] ||
    ACTIVE_DOT_CLASSES.terracotta

  return (
    <section
      className={`relative [&_.slick-dotted.slick-slider]:mb-0! [&_.slick-slide>div]:h-full! [&_.slick-dots]:bottom-7! [&_.slick-dots]:z-10 [&_.slick-dots_li_button::before]:text-linen! [&_.slick-dots_li_button::before]:opacity-100! ${activeDotClass}`}
    >
      <Slider {...CAROUSEL_SETTINGS} afterChange={setActiveIndex}>
        {quotes.map((quote, index) => (
          <div key={`${quote.personName ?? 'quote'}-${index}`}>
            <QuoteCard data={quote} />
          </div>
        ))}
      </Slider>
    </section>
  )
}

export default QuoteSectionRebrand
